import axios from 'axios'
import { API_URL } from '@/utils/constants'
import isAlkanes from '@/utils/is-alkanes'
import isRune from '@/utils/is-rune'
import Outpoint from '@/utils/outpoint'
import { hasRememberedSecureListing } from './secure-ticket-store'

/**
 * Snipe protection (passthrough v4), client side: the capabilities gate,
 * listing detection, and shared helpers. A protected listing is a seller-signed
 * passthrough into a per-listing `tr(NUMS, {multi_a(2, seller, cosigner), recovery})`
 * escrow plus a pre-signed sale child; a purchase spends one such package per
 * item and the buyer signs only their own funding. Verification lives in
 * `secure-passthrough.ts`; the buy/list flows in `secure-ticket-client.ts` and
 * `secure-listing-client.ts`.
 */

/** SIGHASH_SINGLE | ANYONECANPAY — the seller's passthrough v4 sale pre-signature (0x83). */
export const SIGHASH_SINGLE_ANYONECANPAY = 0x83
/** Passthrough v4: per-listing tr(seller, multi_a(2, seller, cosigner)), seller paid in the sale. */
export const PASSTHROUGH_ESCROW_POLICY = 'passthrough_v4'
/** `secure_listing_authorizations.version` / `.state` the API marks a live protected listing with. */
const SECURE_LISTING_MARKER_VERSION = 2
const SECURE_LISTING_MARKER_STATE = 'listed'
/** Build 2 lockbox policies: older, still live, never snipe-protected. */
const BUILD2_LOCKBOX_POLICIES = new Set(['observed_two_of_three', 'two_service_or_seller_csv'])

export type SecureAssetProtocol = 'ordinal' | 'rune' | 'alkane' | 'tap' | 'brc20'

export class SecurePurchaseUnavailableError extends Error {
	readonly code: string

	constructor(code: string, message?: string) {
		super(message || 'Snipe protection is unavailable')
		this.name = 'SecurePurchaseUnavailableError'
		this.code = code
	}
}

/**
 * Snipe protection has no build-time flag. The API is the only gate: a
 * deployment offers it exactly when `GET /market/secure-purchase/capabilities`
 * reports passthrough v4, so production stays closed until an operator flips
 * the API flag, and a preview needs no rebuild to change.
 *
 * Sellers opt in per listing (the snipe-protection toggle). Buyers never
 * choose: the buy path follows what the listing already is, which is why
 * `isSnipeProtectedListing` reads listing data and not a flag.
 */

/**
 * Refuse the legacy `0x83` buy path for a listing we know is snipe-protected.
 * Defence in depth: the buy call sites already branch on
 * `isSnipeProtectedListing` against full listing rows, but the wallet
 * entrypoints only ever see ids/outpoints, so this catches a protected
 * outpoint that reached the legacy path anyway rather than letting it
 * silently settle as `0x83`.
 */
export function assertLegacyPurchaseBlocked(identifiers: string[] = []): void {
	const protectedOutpoint = identifiers
		.map((value) => canonicalBitcoinOutpoint(String(value || '')))
		.find((outpoint) => Boolean(outpoint) && hasRememberedSecureListing(outpoint as string))
	if (protectedOutpoint) {
		throw new SecurePurchaseUnavailableError(
			'legacy_purchase_blocked',
			'This listing is snipe protected and cannot be bought through the standard path'
		)
	}
}

const UNSUPPORTED_SECURE_WALLETS = new Set(['dogelabs', 'nintondo'])

/**
 * Every BTC wallet lists and buys through its own explicit signing path in
 * `secure-sign.ts`, so one gate covers both actions.
 */
export function assertSecureWalletSupported(walletType: string): void {
	if (UNSUPPORTED_SECURE_WALLETS.has(walletType)) {
		throw new SecurePurchaseUnavailableError(
			'unsupported_wallet',
			`${walletType} cannot sign snipe-protected transactions. Use Ordinals Wallet, TAP, UniSat, Xverse, OKX, OYL, or Phantom.`
		)
	}
}

export function compressedPublicKey(hex: string): string {
	const value = (hex || '').trim().toLowerCase().replace(/^0x/, '')
	if (/^(02|03)[0-9a-f]{64}$/.test(value)) {
		return value
	}
	if (/^[0-9a-f]{64}$/.test(value)) {
		return `02${value}`
	}
	throw new SecurePurchaseUnavailableError(
		'invalid_public_key',
		'Seller public key must be 33-byte compressed hex'
	)
}

const BITCOIN_OUTPOINT = /^[0-9a-f]{64}:(0|[1-9][0-9]*)$/i
const HEX_OUTPOINT = /^[0-9a-f]{72}$/i

function bitcoinOutpointFromHex(hex: string): string {
	const { txid, vout } = Outpoint.decode(hex)
	return `${txid}:${vout}`
}

function canonicalBitcoinOutpoint(value: string): string | null {
	if (!value) return null
	if (BITCOIN_OUTPOINT.test(value)) return value.toLowerCase()
	if (HEX_OUTPOINT.test(value)) {
		try {
			return bitcoinOutpointFromHex(value)
		} catch {
			return null
		}
	}
	return null
}

export interface SecurePurchaseCapabilities {
	mode?: string
	customer_enabled?: boolean
	listing_enabled?: boolean
	build_enabled?: boolean
	submit_enabled?: boolean
	escrow_policy?: string
	policy?: string
	/** Passthrough v4 co-signer x-only key; the client pins it. */
	cosigner_public_key?: string
	protocols?: string[]
	protocol_status?: Record<string, string>
}

interface SecureCapabilitiesResponse {
	secure_purchase?: SecurePurchaseCapabilities
	error?: boolean
	code?: string
	message?: string
}

export function parseSecureCapabilities(payload: unknown): SecurePurchaseCapabilities {
	if (!payload || typeof payload !== 'object') {
		throw new SecurePurchaseUnavailableError(
			'invalid_capabilities',
			'Secure capabilities response was empty'
		)
	}
	const body = payload as SecureCapabilitiesResponse
	if (body.error) {
		throw new SecurePurchaseUnavailableError(
			body.code || 'capabilities_error',
			body.message || 'Secure purchase capabilities unavailable'
		)
	}
	if (!body.secure_purchase || typeof body.secure_purchase !== 'object') {
		throw new SecurePurchaseUnavailableError(
			'missing_secure_purchase',
			'Capabilities did not include secure_purchase'
		)
	}
	return body.secure_purchase
}

function escrowPolicyOf(caps: SecurePurchaseCapabilities): string | undefined {
	return caps.escrow_policy || caps.policy
}

export function isBuild3Available(
	caps: SecurePurchaseCapabilities,
	protocol: SecureAssetProtocol
): boolean {
	if (!caps.customer_enabled || !caps.listing_enabled || !caps.submit_enabled) {
		return false
	}
	if (caps.build_enabled === false) {
		return false
	}

	const policy = escrowPolicyOf(caps)
	if (
		[caps.escrow_policy, caps.policy].some(
			(value) => value && BUILD2_LOCKBOX_POLICIES.has(String(value))
		)
	) {
		return false
	}
	if (caps.mode === 'lockbox' || caps.mode === 'two_of_three') {
		return false
	}
	// This client speaks passthrough v4 only. A v3 (two_of_two_ow) API must
	// not be driven by it: the templates and the buyer's verification differ.
	if (policy !== PASSTHROUGH_ESCROW_POLICY) {
		return false
	}
	if (!/^[0-9a-f]{64}$/i.test(String(caps.cosigner_public_key || ''))) {
		return false
	}

	const protocolStatus = caps.protocol_status?.[protocol]
	if (protocolStatus === 'unsafe_seller_recovery_policy') {
		return false
	}
	if (protocolStatus && protocolStatus !== 'enabled') {
		return false
	}
	if (
		Array.isArray(caps.protocols) &&
		caps.protocols.length > 0 &&
		!caps.protocols.includes(protocol)
	) {
		return false
	}

	return true
}

export function assertBuild3Available(
	caps: SecurePurchaseCapabilities,
	protocol: SecureAssetProtocol
): void {
	if (!isBuild3Available(caps, protocol)) {
		throw new SecurePurchaseUnavailableError(
			'build3_unavailable',
			'Snipe protection is unavailable for this listing'
		)
	}
}

/**
 * The capabilities probe is a GET, so it is safe to retry. The API runs a
 * single replica and restarts on every deploy, which is a window of seconds
 * where a seller would otherwise be told snipe protection is broken.
 */
// ~10s total: covers an API rollout restart (single replica).
const CAPABILITIES_RETRY_DELAYS_MS = [300, 900, 1800, 3000, 4000]

async function fetchSecurePurchaseCapabilities(): Promise<SecurePurchaseCapabilities> {
	let lastTransportError: unknown
	for (let attempt = 0; attempt <= CAPABILITIES_RETRY_DELAYS_MS.length; attempt += 1) {
		try {
			const { data, status } = await axios.get(`${API_URL}/market/secure-purchase/capabilities`, {
				headers: { 'Cache-Control': 'no-store' },
				validateStatus: () => true
			})
			// 5xx is the restarting API; 4xx is a real answer and must not retry.
			if (status >= 500) {
				lastTransportError = new Error(`http_${status}`)
			} else if (status >= 400) {
				throw new SecurePurchaseUnavailableError(
					data?.code || `http_${status}`,
					data?.message || 'Secure purchase capabilities unavailable'
				)
			} else {
				return parseSecureCapabilities(data)
			}
		} catch (error) {
			if (error instanceof SecurePurchaseUnavailableError) {
				throw error
			}
			lastTransportError = error
		}
		const delay = CAPABILITIES_RETRY_DELAYS_MS[attempt]
		if (delay !== undefined) {
			await new Promise((resolve) => setTimeout(resolve, delay))
		}
	}
	void lastTransportError
	throw new SecurePurchaseUnavailableError(
		'capabilities_fetch_failed',
		'Snipe protection is unavailable right now. Try again in a moment, or list without protection.'
	)
}

export async function requireBuild3(
	protocol: SecureAssetProtocol
): Promise<SecurePurchaseCapabilities> {
	const caps = await fetchSecurePurchaseCapabilities()
	assertBuild3Available(caps, protocol)
	return caps
}

/**
 * Cached capabilities probe for UI gating.
 *
 * The seller-facing toggle has to decide whether to render before the seller
 * does anything, so this must not throw and must not re-request per
 * component. A failed probe resolves to "unavailable" — fail closed, same as
 * the settlement path — and is not cached, so a transient outage recovers on
 * the next mount instead of pinning the toggle off for the session.
 */
const CAPABILITIES_TTL_MS = 60_000
let capabilitiesCache: { at: number; value: Promise<SecurePurchaseCapabilities> } | undefined

function cachedCapabilities(now: number): Promise<SecurePurchaseCapabilities> {
	if (capabilitiesCache && now - capabilitiesCache.at < CAPABILITIES_TTL_MS) {
		return capabilitiesCache.value
	}
	const value = fetchSecurePurchaseCapabilities()
	capabilitiesCache = { at: now, value }
	// Do not let a rejection stick in the cache for the whole TTL.
	value.catch(() => {
		if (capabilitiesCache?.value === value) {
			capabilitiesCache = undefined
		}
	})
	return value
}

export async function isSnipeProtectionOffered(protocol: SecureAssetProtocol): Promise<boolean> {
	try {
		return isBuild3Available(await cachedCapabilities(Date.now()), protocol)
	} catch {
		return false
	}
}

export function protocolFromInscription(inscription: {
	brc20?: { ticker?: string }
	collection?: { slug?: string }
	rune?: boolean
	alkane?: boolean
}): SecureAssetProtocol {
	const slug = inscription?.collection?.slug || ''
	if (inscription?.brc20?.ticker || slug.startsWith('brc20-')) return 'brc20'
	if (slug.startsWith('tap-')) return 'tap'
	if (inscription?.rune || isRune(slug)) return 'rune'
	if (inscription?.alkane || isAlkanes(slug)) return 'alkane'
	return 'ordinal'
}

export function protocolFromSlug(
	slug?: string,
	extra?: { rune?: boolean | ''; alkane?: boolean | '' }
): SecureAssetProtocol {
	return protocolFromInscription({
		collection: slug ? { slug } : undefined,
		rune: Boolean(extra?.rune),
		alkane: Boolean(extra?.alkane)
	})
}

export async function resolveBitcoinOutpoint(value: string): Promise<string> {
	const direct = canonicalBitcoinOutpoint(value)
	if (direct) return direct

	const { data } = await axios.get(`${API_URL}/inscription/${value}/outpoint`, {
		headers: { 'Cache-Control': 'no-store' }
	})
	const hex = data?.inscription?.outpoint || data?.outpoint
	const resolved = canonicalBitcoinOutpoint(typeof hex === 'string' ? hex : '')
	if (!resolved) {
		throw new SecurePurchaseUnavailableError(
			'missing_outpoint',
			'Could not resolve the current Bitcoin outpoint for this inscription'
		)
	}
	return resolved
}

/** Buyer-facing text for API codes whose server message is the generic
 *  "secure purchase rejected" / "secure purchase unavailable". */
const SECURE_CODE_MESSAGES: Record<string, string> = {
	listing_source_spent:
		'This item was just sold or its listing was moved, so it is no longer available.',
	listing_not_found: 'This item is no longer listed.',
	listings_conflict: 'These two cannot be bought together. Buy them separately.',
	insufficient_funds:
		'Not enough spendable balance to cover the price plus fees. UTXOs holding inscriptions or runes cannot be used.',
	two_funding_utxos_required:
		'A protected purchase needs at least two spendable UTXOs; send yourself a small amount first, then try again.',
	too_many_pending_purchases:
		'This wallet has too many unfinished protected purchases; wait a few minutes and try again.',
	seller_presignature_invalid:
		"The seller's listing signature is invalid; the item cannot be bought right now.",
	escrow_mismatch: 'This listing changed. Refresh and try again.',
	passthrough_missing: 'This listing changed. Refresh and try again.',
	passthrough_mismatch: 'This listing changed. Refresh and try again.',
	passthrough_invalid: 'This listing changed. Refresh and try again.',
	invalid_price: 'This listing has an invalid price and cannot be bought right now.',
	invalid_ow_fee: 'Could not compute the market fee. Try again.',
	invalid_creator_royalty: 'Could not compute the creator royalty. Try again.',
	funding_prevout_missing:
		'One of your wallet UTXOs could not be found; refresh your wallet and try again.',
	funding_prevout_mismatch: 'Your wallet balance changed. Refresh and try again.',
	funding_prevout_txid_mismatch: 'Your wallet balance changed. Refresh and try again.',
	cosigner_key_invalid: 'Protected purchases are unavailable right now.',
	seller_key_invalid: "The seller's listing key is invalid; the item cannot be bought right now."
}

const GENERIC_SERVER_MESSAGES = new Set([
	'secure purchase rejected',
	'secure purchase unavailable',
	'secure purchase not found',
	'snipe-protected purchase failed'
])

const humanizeCode = (code: string) => code.replace(/[_-]+/g, ' ').trim()

/** Turn an API error into something a buyer can act on: a specific message
 *  when we know the code, never the bare "secure purchase rejected", and the
 *  code itself so support can look it up. */
export function secureErrorMessage(error: unknown): string {
	const response = (error as { response?: { data?: { message?: string; code?: string } } })
		?.response
	const code = String((error as { code?: string })?.code || response?.data?.code || '').trim()
	const raw = String(
		(error instanceof SecurePurchaseUnavailableError ? error.message : '') ||
			response?.data?.message ||
			(error as { message?: string })?.message ||
			''
	).trim()
	const generic = !raw || GENERIC_SERVER_MESSAGES.has(raw.toLowerCase())
	let text = generic ? SECURE_CODE_MESSAGES[code] || '' : raw
	if (!text) {
		text = code
			? `Purchase could not be built (${humanizeCode(
					code
			  )}). The item may have just sold or changed.`
			: 'Protected purchase failed. Try again.'
	}
	if (code && !text.includes(code)) text = `${text} [${code}]`
	return text
}

export function isSnipeProtectedListing(item: unknown): boolean {
	if (!item || typeof item !== 'object') {
		return false
	}
	const record = item as Record<string, unknown>
	const escrow =
		record.escrow && typeof record.escrow === 'object'
			? (record.escrow as Record<string, unknown>)
			: record
	const protection =
		escrow.protection && typeof escrow.protection === 'object'
			? (escrow.protection as Record<string, unknown>)
			: undefined
	const policy = String(escrow.policy || escrow.escrow_policy || protection?.policy || '')
	if (BUILD2_LOCKBOX_POLICIES.has(policy)) {
		return false
	}
	// What the API actually emits on market, collection, and detail rows:
	// a server-computed `protected` boolean, plus the raw
	// `secure_purchase_version` / `secure_purchase_state` marker it is derived
	// from (`escrows::secure_listing_is_protected`). Buyers only ever see
	// these; `policy` / `protection` are not on listing rows today.
	if (escrow.protected === true) {
		return true
	}
	if (
		Number(escrow.secure_purchase_version) === SECURE_LISTING_MARKER_VERSION &&
		escrow.secure_purchase_state === SECURE_LISTING_MARKER_STATE
	) {
		return true
	}
	if (policy === PASSTHROUGH_ESCROW_POLICY || protection?.mode === PASSTHROUGH_ESCROW_POLICY) {
		return true
	}
	const outpoint = canonicalBitcoinOutpoint(String(escrow.outpoint || record.outpoint || ''))
	return Boolean(outpoint && hasRememberedSecureListing(outpoint))
}

/**
 * Collections where sellers are offered snipe protection, from
 * `NEXT_PUBLIC_SNIPE_PROTECTION_COLLECTIONS`: `*` (or unset) for every
 * collection, otherwise a comma-separated list of slugs. This is the rollout
 * dial: the preview runs with `*`, production starts with one collection.
 * Buying a protected listing is never gated; the listing decides that.
 */
export function snipeProtectionRolloutCollections(
	raw = process.env.NEXT_PUBLIC_SNIPE_PROTECTION_COLLECTIONS
): '*' | Set<string> {
	const value = String(raw ?? '*').trim()
	if (!value || value === '*') return '*'
	return new Set(
		value
			.split(',')
			.map((s) => s.trim().toLowerCase())
			.filter(Boolean)
	)
}

/** Whether a collection is in the rollout. `null` (unknown collection) is allowed only when the rollout is `*`. */
export function isCollectionInSnipeRollout(
	slug: string | null | undefined,
	rollout = snipeProtectionRolloutCollections()
): boolean {
	if (rollout === '*') return true
	if (!slug) return false
	return rollout.has(String(slug).trim().toLowerCase())
}
